import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useCallback, useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import { debounce } from 'lodash';
import * as _ from 'lodash';
import FormHelperText from '@mui/material/FormHelperText';
import { CommonPasswordField, CommonTextField } from 'gemlux-common-components';
import { Box } from '@mui/material';
import { theme } from '../../../theme/theme';
import { authActions, ICredentials } from '../../../redux/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import VisibilityBlack from '../../../assets/img/visibility-black.svg';
import CustomPhoneNumberInput from '../../V1/CustomPhoneNumberInput/CustomPhoneNumberInput';
import AlertSvg from '../../../assets/img/alert.svg';

type IPersonalCredentialProps = {
  formik: FormikProps<ICredentials>;
};

function PersonalCredential({ formik }: IPersonalCredentialProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useAppDispatch();
  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const isUsernameAlreadyExists = _.get(componentStatus, 'isUsernameAlreadyExists', undefined) as boolean;
  const isEmailAlreadyExists = _.get(componentStatus, 'isEmailAlreadyExists', undefined) as boolean;
  const [customPhoneError, setCustomPhoneError] = useState<string | undefined>();

  const checkUsernameTakenDebounce = useCallback(
    debounce((input: string, isUsername: boolean) => {
      dispatch(authActions.checkUsernameOrEmailTakenAsync({ input, isUsername }));
    }, 1000),
    [],
  );

  const checkEmailTakenDebounce = useCallback(
    debounce((input: string, isUsername: boolean) => {
      dispatch(authActions.checkUsernameOrEmailTakenAsync({ input, isUsername }));
    }, 1000),
    [],
  );

  useEffect(() => {
    if (formik.values.username && !formik.errors.username) {
      checkUsernameTakenDebounce(formik.values.username, true);
    }
    return () => {
      checkUsernameTakenDebounce.cancel();
      dispatch(authActions.setComponentStatus({ isUsernameAlreadyExists: undefined }));
    };
  }, [formik.values.username, formik.errors.username, checkUsernameTakenDebounce]);

  useEffect(() => {
    if (formik.values.businessEmail && !formik.errors.businessEmail) {
      checkEmailTakenDebounce(formik.values.businessEmail, false);
    }
    return () => {
      checkEmailTakenDebounce.cancel();
      dispatch(authActions.setComponentStatus({ isEmailAlreadyExists: undefined }));
    };
  }, [formik.values.businessEmail, formik.errors.businessEmail, checkEmailTakenDebounce]);

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('phoneNumber', pNumber);
  };

  const handleErrorChange = (err: string | undefined) => {
    setCustomPhoneError(err);
  };
  return (
    <>
      <Grid container spacing="20px">
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="username">
            Username
          </Typography>
          <CommonTextField
            id="username"
            type="text"
            name="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={(formik.touched.username && Boolean(formik.errors.username)) || isUsernameAlreadyExists}
            helperText={formik.touched.username && formik.errors.username}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
          {isUsernameAlreadyExists && (
            <Box display="flex" flexDirection="row" gap={1}>
              <Box src={AlertSvg} component="img" />
              <FormHelperText sx={{ ...theme.typography.footnoteRegular2, color: '#DF0C3D', mt: 0 }}>
                UserName is already taken!
              </FormHelperText>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={6} />
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="firstName">
            First Name
          </Typography>
          <CommonTextField
            id="firstName"
            type="text"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="familyName">
            Last Name
          </Typography>
          <CommonTextField
            id="familyName"
            type="text"
            name="familyName"
            value={formik.values.familyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.familyName && Boolean(formik.errors.familyName)}
            helperText={formik.touched.familyName && formik.errors.familyName}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="businessEmail">
            Email Address
          </Typography>
          <CommonTextField
            id="businessEmail"
            type="email"
            name="businessEmail"
            value={formik.values.businessEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={(formik.touched.businessEmail && Boolean(formik.errors.businessEmail)) || isEmailAlreadyExists}
            helperText={formik.touched.businessEmail && formik.errors.businessEmail}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
          {isEmailAlreadyExists && (
            <Box display="flex" flexDirection="row" gap={1}>
              <Box src={AlertSvg} component="img" />
              <FormHelperText sx={{ ...theme.typography.footnoteRegular2, color: '#DF0C3D', mt: 0 }}>
                Email Address is already taken!
              </FormHelperText>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="confirmEmail">
            Confirm Email Address
          </Typography>
          <CommonTextField
            id="confirmEmail"
            type="email"
            name="confirmEmail"
            value={formik.values.confirmEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmEmail && Boolean(formik.errors.confirmEmail)}
            helperText={formik.touched.confirmEmail && formik.errors.confirmEmail}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            autoComplete="off"
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500">Mobile Number</Typography>
          <CustomPhoneNumberInput
            name="phoneNumber"
            value={formik.values.phoneNumber as string}
            handleChange={handlePhoneNumberChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.phoneNumber || customPhoneError ? formik.errors.phoneNumber || customPhoneError : undefined
            }
            onErrorChange={handleErrorChange}
          />
        </Grid>
      </Grid>
      <Grid container spacing="20px">
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="password">
            Password
          </Typography>
          <CommonPasswordField
            id="password"
            name="password"
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            visibilityIcon={<Box component="img" src={VisibilityBlack} />}
            errorBorderColor="#DF0C3D"
            errorTextColor="#DF0C3D"
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="confirmPassword">
            Confirm Password
          </Typography>
          <CommonPasswordField
            id="confirmPassword"
            name="confirmPassword"
            showPassword={showConfirmPassword}
            setShowPassword={setShowConfirmPassword}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            visibilityIcon={<Box component="img" src={VisibilityBlack} />}
            errorBorderColor="#DF0C3D"
            errorTextColor="#DF0C3D"
          />
        </Grid>
      </Grid>
    </>
  );
}

export default PersonalCredential;
